import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface TimeframePanelProps {
  selectedTimeframe: string;
  onTimeframeChange: (timeframe: string) => void;
  selectedChartType: string;
  onChartTypeChange: (chartType: string) => void;
  isRightPanelVisible?: boolean;
  isFullChartView?: boolean;
}

const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1H', '4H', '6H', '12H', '1D', 'W', '3W', 'M', '2M', '3M', '6M', 'Y'];
const CHART_TYPES = ['line', 'area', 'candlestick'];

/**
 * Space the two arrows take, including the gaps either side of the strip.
 *
 * Both arrows are always rendered once they are needed — disabled at the ends rather than
 * removed — so this is a constant. Removing one at the end of the run would give the strip
 * back its width and shift every button sideways at the exact moment the user is trying to
 * click one.
 */
const ARROWS_WIDTH = 64;

/**
 * Timeframe and chart-type selectors.
 *
 * How many timeframes fit is measured rather than guessed. This used to switch between three,
 * eight, ten and all sixteen on `window.innerWidth` breakpoints, which is not the width this
 * strip actually has: the side panels take a few hundred pixels out of it without changing the
 * window at all, and the fixed counts left visible space unused on some widths while
 * overflowing on others. Now the full strip is laid out, its width compared against the space
 * available, and the arrows appear only when it genuinely does not fit.
 *
 * All sixteen buttons are always in the DOM, inside a clipped viewport that the strip slides
 * within, so paging is a transform rather than a different slice of the array — which is what
 * makes it animate.
 */
const TimeframePanel: React.FC<TimeframePanelProps> = ({
  selectedTimeframe,
  onTimeframeChange,
  selectedChartType,
  onChartTypeChange,
  isFullChartView = false,
}) => {
  const { theme } = useTheme();

  const areaRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  const [areaWidth, setAreaWidth] = useState(0);
  const [stripWidth, setStripWidth] = useState(0);
  /** Leftmost timeframe in view; the strip is slid so this one starts at the viewport edge. */
  const [startIndex, setStartIndex] = useState(0);
  const [offset, setOffset] = useState(0);

  const needsArrows = areaWidth > 0 && stripWidth > areaWidth;
  const viewportWidth = needsArrows ? Math.max(0, areaWidth - ARROWS_WIDTH) : stripWidth;
  const maxOffset = Math.max(0, stripWidth - viewportWidth);

  // Both widths are watched rather than read once: the strip's changes with the font and the
  // area's with every panel that opens or closes beside it.
  useEffect(() => {
    const area = areaRef.current;
    const strip = stripRef.current;
    if (!area || !strip) return;

    let frame: number | null = null;

    const measure = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = null;
        // Sub-pixel differences are rounding, and reacting to them would keep this looping.
        setAreaWidth(previous => (Math.abs(previous - area.clientWidth) < 1 ? previous : area.clientWidth));
        setStripWidth(previous =>
          Math.abs(previous - strip.scrollWidth) < 1 ? previous : strip.scrollWidth
        );
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(area);
    observer.observe(strip);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  /**
   * Where a given timeframe's button starts, relative to the strip.
   *
   * Measured against the first button rather than read from `offsetLeft` directly. That
   * property is relative to the nearest *positioned* ancestor, and the strip is not one, so
   * on its own it returns a figure that includes everything to the left of the panel as well.
   * Every offset then came out larger than the strip's own scroll range, the slide clamped to
   * its end on the first frame, and the arrows moved nothing. The buttons all share whatever
   * that ancestor is, so the difference between two of them is the distance within the strip
   * however the panel happens to be positioned.
   */
  const buttonOffset = useCallback((index: number): number => {
    const strip = stripRef.current;
    const button = strip?.children[index] as HTMLElement | undefined;
    const first = strip?.children[0] as HTMLElement | undefined;
    if (!button || !first) return 0;
    return button.offsetLeft - first.offsetLeft;
  }, []);

  /**
   * The furthest the strip can start from, in button indices.
   *
   * Paging is clamped to this rather than to the last timeframe. The slide stops at
   * `maxOffset` well before the sixteenth button reaches the left edge, so allowing the index
   * past this point would let it run on invisibly, and the user would then have to press the
   * left arrow several times before anything moved.
   */
  const lastStartIndex = useCallback((): number => {
    const strip = stripRef.current;
    if (!strip) return 0;

    for (let index = strip.children.length - 1; index >= 0; index--) {
      if (buttonOffset(index) <= maxOffset) return index;
    }
    return 0;
  }, [maxOffset, buttonOffset]);

  // The slide itself. Measured from the DOM rather than from an assumed button width, because
  // "15m" and "1Y" are not the same width.
  useLayoutEffect(() => {
    setOffset(Math.min(buttonOffset(startIndex), maxOffset));
  }, [startIndex, maxOffset, stripWidth, areaWidth, buttonOffset]);

  // Selecting a timeframe from elsewhere — a keyboard shortcut, a restored session — should
  // bring it into view rather than leave the strip parked somewhere else.
  //
  // Only when the selection actually changes, though. This also depends on the offset, so
  // without the guard it ran again the moment an arrow slid the strip: paging right pushed the
  // selected timeframe off the left edge, which this read as "out of view" and immediately
  // undid. The arrows appeared to do nothing at all.
  const broughtIntoView = useRef<string | null>(null);

  useEffect(() => {
    if (!needsArrows) return;
    if (broughtIntoView.current === selectedTimeframe) return;
    broughtIntoView.current = selectedTimeframe;

    const index = TIMEFRAMES.indexOf(selectedTimeframe);
    if (index < 0) return;

    const button = stripRef.current?.children[index] as HTMLElement | undefined;
    if (!button) return;

    const left = buttonOffset(index);
    const right = left + button.offsetWidth;

    if (left < offset || right > offset + viewportWidth) {
      setStartIndex(Math.min(index, lastStartIndex()));
    }
  }, [selectedTimeframe, needsArrows, offset, viewportWidth, lastStartIndex, buttonOffset]);

  const canGoLeft = needsArrows && offset > 0.5;
  const canGoRight = needsArrows && offset < maxOffset - 0.5;

  const navigate = (direction: 'left' | 'right') => {
    const limit = lastStartIndex();
    setStartIndex(previous => {
      const next = direction === 'left' ? previous - 1 : previous + 1;
      return Math.min(Math.max(next, 0), limit);
    });
  };

  const arrow = (direction: 'left' | 'right', enabled: boolean) => (
    <button
      onClick={() => navigate(direction)}
      disabled={!enabled}
      className={`p-1 text-xs transition-colors duration-200 ${
        enabled ? 'theme-text-quaternary hover:theme-text-primary' : 'opacity-30 cursor-default theme-text-quaternary'
      }`}
      title={direction === 'left' ? 'Previous timeframes' : 'Next timeframes'}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={direction === 'left' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
        />
      </svg>
    </button>
  );

  return (
    <div className={`flex flex-row items-center justify-between space-x-3 mt-2 ${isFullChartView ? 'flex-nowrap' : ''}`}>
      {/* Timeframe Selector - Left side. `min-w-0` lets it be narrower than its contents,
          which is what allows the strip to be clipped instead of pushing the row wider. */}
      <div ref={areaRef} className="flex items-center space-x-2 flex-1 min-w-0">
        {needsArrows && arrow('left', canGoLeft)}

        <div
          className="overflow-hidden"
          style={needsArrows ? { width: `${viewportWidth}px`, flexShrink: 0 } : undefined}
        >
          <div
            ref={stripRef}
            className="flex rounded-md border theme-border w-max transition-transform duration-300 ease-out"
            style={{ transform: `translateX(${-offset}px)` }}
          >
            {TIMEFRAMES.map((tf, index) => (
              <button
                key={tf}
                onClick={() => onTimeframeChange(tf)}
                className={`px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                  selectedTimeframe === tf
                    ? theme === 'light' ? 'bg-white text-gray-900' : 'bg-[#000000] text-white'
                    : `theme-text-quaternary ${
                        theme === 'light' 
                          ? 'hover:text-[#2AB7A6] hover:bg-gray-50' 
                          : 'hover:theme-text-primary hover:theme-bg-quaternary'
                      }`
                } ${index === 0 ? 'rounded-l-md' : ''} ${
                  index === TIMEFRAMES.length - 1 ? 'rounded-r-md' : ''
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>

        {needsArrows && arrow('right', canGoRight)}
      </div>

      {/* Chart Type Selector - Right side */}
      <div className="flex justify-end flex-shrink-0">
        <div className="flex rounded-md border theme-border">
          {CHART_TYPES.map(type => (
            <button
              key={type}
              onClick={() => onChartTypeChange(type)}
              className={`px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                selectedChartType === type
                  ? theme === 'light' ? 'bg-white text-gray-900' : 'bg-[#000000] text-white'
                  : 'theme-text-quaternary hover:theme-text-primary hover:theme-bg-quaternary'
              } ${type === CHART_TYPES[0] ? 'rounded-l-md' : ''} ${
                type === CHART_TYPES[CHART_TYPES.length - 1] ? 'rounded-r-md' : ''
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimeframePanel;
